import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@betareader/design-system';
import styled from 'styled-components';
import Icon from '../AppIcon';

/**
 * The shared empty / loading / error shells for Critique Groups (design
 * cluster I). They share design-system controls and appear across critique
 * screens to keep one voice.
 */

const Centered = styled(Card)`
  &&& {
    text-align: center;
    padding: 40px 26px;
    border-radius: 14px;
  }
`;

const IconCircle = styled.div<{ $tone?: 'teal' | 'red' | 'quiet' }>`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: ${({ $tone }) =>
    $tone === 'red' ? 'var(--br-error-surface)' : 'var(--br-raised-surface)'};
  color: ${({ $tone }) =>
    $tone === 'red' ? 'var(--br-error-text)' :
      $tone === 'quiet' ? 'var(--br-muted)' : 'var(--br-accent)'};
  border: ${({ $tone }) => ($tone === 'quiet' ? '1px solid var(--br-border)' : 'none')};
`;

const Skeleton = styled.div`
  display: grid;
  gap: 9px;
  > span {
    display: block;
    height: 12px;
    border-radius: var(--br-control-radius);
    background: var(--br-raised-surface);
  }
  > span:first-child { width: 42%; height: 24px; }
  > span:nth-child(2) { width: 68%; }
  > span:nth-child(3n) { width: 82%; }
`;

const Title = styled.h4`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
`;

const Body = styled.p`
  margin: 8px 0 16px;
  font-size: 13.5px;
  color: var(--br-muted);
  line-height: 1.55;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
`;

type EmptyStateProps = {
  icon: string;
  title: string;
  body?: string;
  tone?: 'teal' | 'red' | 'quiet';
  action?: React.ReactNode;
  footnote?: string;
  'data-testid'?: string;
};

export const CritiqueEmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  body,
  tone = 'teal',
  action,
  footnote,
  ...rest
}) => (
  <Centered data-testid={rest['data-testid'] || 'critique-empty-state'}>
    <IconCircle $tone={tone}>
      <Icon name={icon} style={{ margin: 0 }} />
    </IconCircle>
    <Title>{title}</Title>
    {body && <Body>{body}</Body>}
    {action}
    {footnote && (
      <div style={{ marginTop: 14, fontSize: 12, color: '#a49f93' }}>
        {footnote}
      </div>
    )}
  </Centered>
);

/** Skeleton — mirrors the shape of a group card so the swap isn't jarring. */
export const CritiqueLoadingState: React.FC<{ lines?: number }> = ({
  lines = 3,
}) => {
  const { t } = useTranslation();
  return (
    <Card data-testid="critique-loading-state" role="status">
      <Skeleton aria-hidden="true">
        {Array.from({ length: lines + 2 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} />
        ))}
      </Skeleton>
      <div
        style={{
          textAlign: 'center',
          marginTop: 20,
          color: '#b7b2a6',
          fontSize: 12.5,
        }}
      >
        <Icon name="spinner" loading />
        {t('CritiqueLoading', 'Loading your circle…')}
      </div>
    </Card>
  );
};

type ErrorStateProps = {
  /** Shown instead of the generic line when the server sent a usable message. */
  message?: string;
  onRetry?: () => void;
};

/**
 * The reassurance ("your feedback is safe") is deliberate: this screen is most
 * often seen mid-critique, and the first fear is having lost written work.
 */
export const CritiqueErrorState: React.FC<ErrorStateProps> = ({
  message,
  onRetry,
}) => {
  const { t } = useTranslation();
  return (
    <CritiqueEmptyState
      data-testid="critique-error-state"
      tone="red"
      icon="plug"
      title={t('CritiqueLoadFailed', "We couldn't load this group")}
      body={
        message ||
        t(
          'CritiqueLoadFailedBody',
          'Something went wrong on our end. Your feedback is safe — nothing was lost.'
        )
      }
      action={
        onRetry ? (
          <Button onClick={onRetry}>
            <Icon name="redo" />
            {t('TryAgain', 'Try again')}
          </Button>
        ) : undefined
      }
    />
  );
};
